import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import { useAppDispatch } from '../../app/hooks';
import { baseApi } from '../../services/baseApi';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  ArrowLeftIcon,
  CaretIcon,
  EditSquareIcon,
  CheckmarkIcon,
  StatusInProgressIcon,
  StatusPendingIcon,
  StatusAssignedIcon,
  PaperclipIcon,
  DownloadIcon,
} from '../../assets/icons';
import CommentsPanel from '../../components/CommentsPanel';
import ActivityHistoryPanel from '../../components/ActivityHistoryPanel';
import { useGetEngagementQuery, engName } from '../../features/engagements/engagementsApi';
import {
  useGetTaskQuery,
  useUpdateTaskMutation,
  taskTitle,
  taskDesc,
  taskLocation,
  taskDate,
  taskRawStatus,
  taskAssigneeName,
  statusKey,
  API_STATUS,
} from '../../features/tasks/tasksApi';

type SubStatus = 'completed' | 'inprogress' | 'pending' | 'assigned';
const toSubStatus = (s: string): SubStatus => {
  const k = statusKey(s);
  return k === 'scheduled' || k === 'planning' ? 'pending' : k;
};

const STATUS_META: Record<SubStatus, { label: string; className: string }> = {
  completed: { label: 'Completed', className: 'bg-[#dbeafe] text-[#2563eb]' },
  inprogress: { label: 'In Progress', className: 'bg-[#e0e7ff] text-[#0f52ba]' },
  pending: { label: 'Pending', className: 'bg-[#f3f4f6] text-[#6b7280]' },
  assigned: { label: 'Assigned', className: 'bg-[#eef4ff] text-[#4b5563]' },
};

/** Two-letter initials for an avatar (the API carries no photo). */
function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function StatusPill({ status }: { status: SubStatus }) {
  const meta = STATUS_META[status];
  const icon =
    status === 'completed' ? (
      <CheckmarkIcon className="size-3" />
    ) : status === 'inprogress' ? (
      <StatusInProgressIcon className="size-3.5" />
    ) : status === 'pending' ? (
      <StatusPendingIcon className="size-3.5" />
    ) : (
      <StatusAssignedIcon className="size-3.5" />
    );
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
        meta.className,
      ].join(' ')}
    >
      {icon}
      {meta.label}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.55px] text-[#64748b]">
        {label}
      </span>
      {children}
    </div>
  );
}

export default function SubTaskDetailPage() {
  const navigate = useNavigate();
  const { id, taskId, subId } = useParams();

  const { data: engagement } = useGetEngagementQuery(id!, { skip: !id });
  const { data: parentTask } = useGetTaskQuery(taskId!, { skip: !taskId });
  const { data: sub } = useGetTaskQuery(subId!, { skip: !subId });
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const dispatch = useAppDispatch();

  const engagementName = engagement ? engName(engagement) : 'Engagement';
  const taskName = parentTask ? taskTitle(parentTask) : 'Task';
  const subName = sub ? taskTitle(sub) : 'Sub task';
  const detail = sub ? taskLocation(sub) : '';
  const assignee = sub ? taskAssigneeName(sub) : '';

  const engagementPath = `/user/engagements/${id ?? ''}`;
  const taskPath = `/user/engagements/${id ?? ''}/tasks/${taskId ?? ''}`;

  // Local status mirrors the server value and updates it when changed.
  const [status, setStatus] = useState<SubStatus>('pending');
  useEffect(() => {
    if (sub) setStatus(toSubStatus(taskRawStatus(sub)));
  }, [sub]);

  const changeStatus = async (next: SubStatus) => {
    const prev = status;
    setStatus(next);
    if (!subId) return;
    try {
      await updateTask({ id: subId, patch: { task_status: API_STATUS[next] } }).unwrap();
      // The status change is logged server-side — refresh the Activity History now.
      dispatch(baseApi.util.invalidateTags(['ActivityLog']));
    } catch {
      setStatus(prev);
      message.error('Could not update status. Please try again.');
    }
  };

  const statusItems: MenuProps['items'] = (
    ['completed', 'inprogress', 'pending', 'assigned'] as SubStatus[]
  ).map((s) => ({ key: s, label: STATUS_META[s].label }));

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs
        items={[
          { label: 'Engagements', to: '/user/engagements' },
          { label: engagementName, to: engagementPath },
          { label: taskName, to: taskPath },
          { label: subName },
        ]}
      />

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(taskPath, { state: { name: engagementName, taskName } })}
            className="text-ink transition-colors hover:text-brand"
          >
            <ArrowLeftIcon className="size-7" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-2xl font-semibold text-ink">{subName}</h1>
            {detail && <span className="text-sm text-ink-secondary">{detail}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={status} />
          <button
            type="button"
            onClick={() =>
              navigate(`${taskPath}/subtasks/${subId ?? ''}/edit`, {
                state: { name: engagementName, taskName },
              })
            }
            className="flex items-center gap-2 rounded-lg border border-brand px-4 py-2 text-sm font-medium text-brand transition-colors hover:bg-brand-shade"
          >
            <EditSquareIcon className="size-4" />
            Edit
          </button>
        </div>
      </div>

      {/* Overview */}
      <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
          <Field label="Status">
            <Dropdown
              menu={{
                items: statusItems,
                selectable: true,
                selectedKeys: [status],
                onClick: ({ key }) => changeStatus(key as SubStatus),
              }}
              trigger={['click']}
              disabled={isUpdating}
            >
              <button type="button" className="flex items-center gap-1.5 self-start">
                <StatusPill status={status} />
                <CaretIcon className="size-4 text-ink-secondary" />
              </button>
            </Dropdown>
          </Field>
          <Field label="Assigned To">
            {assignee ? (
              <div className="flex items-center gap-2">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-shade text-[10px] font-semibold text-brand">
                  {initials(assignee)}
                </span>
                <span className="text-sm font-medium text-[#334155]">{assignee}</span>
              </div>
            ) : (
              <span className="text-sm italic text-[#9ca3af]">Unassigned</span>
            )}
          </Field>
          <Field label="Due Date">
            <span className="text-sm font-medium text-[#334155]">{(sub && taskDate(sub)) || '—'}</span>
          </Field>
        </div>

        <div className="mt-6 border-t border-[#f3f4f6] pt-6">
          <h3 className="text-sm font-semibold text-ink">Description</h3>
          <p className="mt-2 max-w-[720px] text-sm leading-[22px] text-ink-secondary">
            {(sub && taskDesc(sub)) || 'No description provided.'}
          </p>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Documentation */}
          <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <div className="flex items-center gap-2">
              <PaperclipIcon className="size-5 text-[#0f172a]" />
              <h2 className="text-base font-semibold text-[#0f172a]">Documentation</h2>
            </div>
            {sub?.attachments && sub.attachments.length > 0 ? (
              <div className="mt-4 flex flex-col gap-3">
                {sub.attachments.map((a, idx) => (
                  <a
                    key={a.id ?? idx}
                    href={a.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between rounded-lg bg-brand-shade p-4 transition-colors hover:bg-[#e2ecff]"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex size-10 items-center justify-center rounded bg-white text-xs font-bold uppercase text-[#ef4444] shadow-card">
                        {(a.file_type || 'file').slice(0, 3)}
                      </span>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-[#0f172a]">
                          {a.name || `Attachment ${idx + 1}`}
                        </span>
                        <span className="text-xs uppercase text-[#64748b]">{a.file_type || 'FILE'}</span>
                      </div>
                    </div>
                    <DownloadIcon className="size-5 text-[#64748b]" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="mt-4 rounded-lg bg-[#f8fafc] p-4 text-sm text-ink-secondary">
                No documents attached.
              </p>
            )}
          </div>

          {/* Comments */}
          {subId && <CommentsPanel entityId={subId} />}
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Assignee */}
          <div className="rounded-xl border border-line-soft bg-white p-6 shadow-card">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-ink">Assignee</h2>
            </div>
            <div className="mt-4 flex items-center gap-3">
              {assignee ? (
                <>
                  <span className="relative shrink-0">
                    <span className="flex size-12 items-center justify-center rounded-full border-2 border-white bg-brand-shade text-sm font-semibold text-brand shadow-card">
                      {initials(assignee)}
                    </span>
                    <span className="absolute bottom-0 right-0 size-3 rounded-full border-2 border-white bg-[#22c55e]" />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-ink">{assignee}</span>
                    <span className="text-xs font-semibold text-brand">Coordinator</span>
                  </div>
                </>
              ) : (
                <span className="text-sm italic text-ink-secondary">Unassigned</span>
              )}
            </div>
          </div>

          {/* Activity History */}
          {subId && <ActivityHistoryPanel entityId={subId} />}
        </div>
      </div>
    </div>
  );
}
